/**
 * Chat Types
 *
 * Types for the Ask Poco AI chat feature (client-scoped and global).
 */

// Message role type matching database enum
export type ChatMessageRole = 'user' | 'assistant' | 'system';

/**
 * A chat conversation, either client-scoped or org-wide
 */
export interface ClientChat {
  id: string;
  client_id: string | null; // null = org-wide conversation
  title: string;
  scope: 'client' | 'global';
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * Extended chat with computed fields from helper function
 */
export interface ClientChatWithCounts extends ClientChat {
  message_count: number;
  last_message_at: string | null;
}

/**
 * A message within a chat conversation
 */
export interface ChatMessage {
  id: string;
  chat_id: string;
  role: ChatMessageRole;
  content: string;
  metadata: ChatMessageMetadata | null;
  context_sources: string[] | null;
  created_by: string | null;
  created_at: string;
}

/**
 * Metadata stored with assistant messages
 */
export interface ChatMessageMetadata {
  model?: string;
  token_count?: number;
  response_time_ms?: number;
  sources_used?: string[];
  retry_attempts?: number;
  [key: string]: unknown;
}

/**
 * Request payload for chat-with-client edge function
 */
export interface SendMessageRequest {
  clientId?: string;
  message: string;
  chatId?: string; // Optional - omit to create new chat
  scope?: 'client' | 'global';
}

/**
 * Response from chat-with-client edge function
 */
export interface ChatResponse {
  chatId: string;
  messageId: string;
  response: string;
  title?: string; // Only present for new chats
  metadata: {
    model: string;
    tokenCount?: number;
    responseTimeMs: number;
    sourcesUsed: string[];
  };
}

/**
 * Error response from chat-with-client edge function
 */
export interface ChatError {
  error: string;
}

/**
 * Sources that can be used for context
 */
export type ChatContextSource =
  | 'client_details'
  | 'chat_history'
  | 'recent_analyses'
  | 'active_tasks'
  | 'client_goals'
  | 'emails'
  | 'slack'
  | 'clickup_tasks'
  | 'financials'
  | 'bd_pipeline'
  | 'team_performance'
  | 'contractor_rates'
  | 'audit_log';

/**
 * Display labels for context sources
 */
export const CONTEXT_SOURCE_LABELS: Record<ChatContextSource, string> = {
  client_details: 'Client Information',
  chat_history: 'Previous Messages',
  recent_analyses: 'Meeting Summaries',
  active_tasks: 'Active Tasks',
  client_goals: 'Client Goals',
  emails: 'Email History',
  slack: 'Slack Messages',
  clickup_tasks: 'ClickUp Tasks',
  financials: 'Financial Data',
  bd_pipeline: 'Business Development',
  team_performance: 'Team Performance',
  contractor_rates: 'Contractor Rates',
  audit_log: 'Audit Log',
};

/**
 * Helper to check if a response is an error
 */
export function isChatError(response: ChatResponse | ChatError): response is ChatError {
  return 'error' in response;
}

/**
 * Helper to format message timestamp
 */
export function formatMessageTime(createdAt: string): string {
  const date = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Helper to format chat title for display
 */
export function formatChatTitle(chat: ClientChat): string {
  return chat.title || 'New Chat';
}

/**
 * Helper to get initials from role for avatar
 */
export function getRoleInitials(role: ChatMessageRole): string {
  switch (role) {
    case 'user':
      return 'U';
    case 'assistant':
      return 'P'; // Poco
    case 'system':
      return 'S';
  }
}
